// The CUSTOM_API provider's configJson, as a form — and the agent binding that uses it.
//
// The backend is the authority: CustomApiConfig.parse validates on every save and
// answers 400 with every problem it found. The checks here mirror the ones a person
// can fix while typing (a bad placeholder, a credential-looking header, a missing
// tool name) so the form says so before the round trip. They must never be STRICTER
// than the server: a config the server accepts has to be saveable from here.
//
// Free of service imports for the same reason as crmProviderShapes.ts — testable
// without axios. Types come in as `import type`, which the compiler erases.
import type { CrmAuthType, CustomApiDeliveryStatus } from 'api/services/crmProviders.service';
import { diffDocuments } from 'pages/Agents/runtimeDocDiff';

export type CustomApiMode = 'PUSH' | 'FETCH';
export type CustomApiBodyFormat = 'JSON' | 'FORM_URLENCODED' | 'XML';
export type CustomApiResponseFormat = 'JSON' | 'XML';

/** One editable name/value row. `orig` keeps a non-string value's type while its text is unchanged. */
export interface KV { key: string; value: string; orig?: unknown }

export interface CustomApiForm {
  mode: CustomApiMode;
  toolName: string;
  method: string;
  url: string;
  requestIdParam: string;
  headers: KV[];
  /** Text so an empty box can mean "the default". */
  timeoutMs: string;
  bodyFormat: CustomApiBodyFormat;
  /** JSON text for JSON / FORM_URLENCODED (bodyTemplate), the XML document for XML (bodyTemplateText). */
  bodyText: string;
  responseFormat: CustomApiResponseFormat;
  successPath: string;
  successValue: string;
  referencePath: string;
  errorPath: string;
  messagePath: string;
  responseFields: KV[];
  sayOnError: string;
  apiKeyHeader: string;
  hmacHeader: string;
  hmacSigns: 'BODY' | 'METHOD_PATH_TIMESTAMP_BODY';
  hmacTimestampHeader: string;
  hmacEncoding: 'hex' | 'base64';
  hmacPrefix: string;
  oauthTokenUrl: string;
  oauthScope: string;
  /** inputRules as JSON text — rare, and an array of regexes is clearer as JSON than as a grid. */
  inputRulesText: string;
  /** Every configJson key this form does not model, sent back verbatim. */
  extra: Record<string, unknown>;
}

export const CUSTOM_API_AUTH: CrmAuthType[] =
  ['NONE', 'API_KEY', 'BEARER', 'BASIC', 'HMAC_SHA256', 'OAUTH2_CLIENT_CREDENTIALS'];

/**
 * Which encrypted field holds what, per auth type (CustomApiClient.java):
 * API_KEY / BEARER / HMAC_SHA256 → refreshToken; BASIC and client credentials →
 * clientId + clientSecret. Neither secret is ever returned — only hasRefreshToken /
 * hasClientSecret.
 */
export const AUTH_FIELDS: Record<string, { label: string; secret?: string; clientId?: string; clientSecret?: string; hint: string }> = {
  NONE: { label: 'None', hint: 'No credentials are sent.' },
  API_KEY: { label: 'API key header', secret: 'API key', hint: 'Sent in the header named below (default X-API-Key).' },
  BEARER: { label: 'Bearer token', secret: 'Token', hint: 'Sent as Authorization: Bearer <token>.' },
  BASIC: { label: 'Basic (user + password)', clientId: 'Username', clientSecret: 'Password', hint: 'Sent as Authorization: Basic.' },
  HMAC_SHA256: { label: 'HMAC-SHA256 signature', secret: 'Shared signing secret', hint: 'Each request is signed; the far end verifies it with the same secret.' },
  OAUTH2_CLIENT_CREDENTIALS: {
    label: 'OAuth2 client credentials', clientId: 'Client ID', clientSecret: 'Client secret',
    hint: 'A token is fetched from the token URL and cached until a minute before it expires.',
  },
};

const KNOWN = new Set([
  'mode', 'toolName', 'method', 'url', 'requestIdParam', 'headers', 'timeoutMs', 'bodyFormat',
  'bodyTemplate', 'bodyTemplateText', 'responseFormat', 'response', 'responseFields', 'sayOnError',
  'apiKeyHeader', 'inputRules', 'hmac', 'oauth',
]);

export const BLANK_FORM: CustomApiForm = {
  mode: 'PUSH', toolName: '', method: 'POST', url: '', requestIdParam: '', headers: [], timeoutMs: '',
  bodyFormat: 'JSON', bodyText: '', responseFormat: 'JSON',
  successPath: '', successValue: '', referencePath: '', errorPath: '', messagePath: '',
  responseFields: [], sayOnError: '', apiKeyHeader: '',
  hmacHeader: '', hmacSigns: 'BODY', hmacTimestampHeader: '', hmacEncoding: 'hex', hmacPrefix: '',
  oauthTokenUrl: '', oauthScope: '', inputRulesText: '', extra: {},
};

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** A value as a row shows it: text for primitives, JSON for anything else. */
const asText = (v: unknown): string => (v !== null && typeof v === 'object' ? JSON.stringify(v) : str(v));

export const rowsFrom = (o: unknown): KV[] =>
  isObj(o) ? Object.entries(o).map(([key, v]) => ({ key, value: asText(v), orig: v })) : [];

/** Rows back to an object. Blank names are dropped; an unchanged row keeps its original type. */
export const objectFrom = (rows: KV[]): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  rows.forEach(r => {
    const k = r.key.trim();
    if (!k) return;
    out[k] = r.orig !== undefined && asText(r.orig) === r.value ? r.orig : r.value;
  });
  return out;
};

/** A stored configJson as the form. Unparseable or empty → the blank form. */
export function formFromConfig(configJson?: string | null): CustomApiForm {
  let c: Record<string, unknown> = {};
  try {
    const parsed = configJson && configJson.trim() ? JSON.parse(configJson) : {};
    if (isObj(parsed)) c = parsed;
  } catch { /* the blank form; the server never stores an unparseable one */ }
  const mode: CustomApiMode = str(c.mode).toUpperCase() === 'FETCH' ? 'FETCH' : 'PUSH';
  const bodyFormat = (['JSON', 'FORM_URLENCODED', 'XML'].includes(str(c.bodyFormat).toUpperCase())
    ? str(c.bodyFormat).toUpperCase() : 'JSON') as CustomApiBodyFormat;
  const r = isObj(c.response) ? c.response : {};
  const h = isObj(c.hmac) ? c.hmac : {};
  const o = isObj(c.oauth) ? c.oauth : {};
  const extra: Record<string, unknown> = {};
  Object.entries(c).forEach(([k, v]) => { if (!KNOWN.has(k)) extra[k] = v; });
  return {
    ...BLANK_FORM,
    mode,
    toolName: str(c.toolName),
    method: str(c.method).toUpperCase() || (mode === 'FETCH' ? 'GET' : 'POST'),
    url: str(c.url),
    requestIdParam: str(c.requestIdParam),
    headers: rowsFrom(c.headers),
    timeoutMs: str(c.timeoutMs),
    bodyFormat,
    bodyText: bodyFormat === 'XML'
      ? str(c.bodyTemplateText)
      : (c.bodyTemplate === undefined || c.bodyTemplate === null ? '' : JSON.stringify(c.bodyTemplate, null, 2)),
    responseFormat: str(c.responseFormat).toUpperCase() === 'XML' ? 'XML' : 'JSON',
    successPath: str(r.successPath), successValue: str(r.successValue), referencePath: str(r.referencePath),
    errorPath: str(r.errorPath), messagePath: str(r.messagePath),
    responseFields: rowsFrom(c.responseFields),
    sayOnError: str(c.sayOnError),
    apiKeyHeader: str(c.apiKeyHeader),
    hmacHeader: str(h.header),
    hmacSigns: str(h.signs).toUpperCase() === 'METHOD_PATH_TIMESTAMP_BODY' ? 'METHOD_PATH_TIMESTAMP_BODY' : 'BODY',
    hmacTimestampHeader: str(h.timestampHeader),
    hmacEncoding: str(h.encoding).toLowerCase() === 'base64' ? 'base64' : 'hex',
    hmacPrefix: str(h.prefix),
    oauthTokenUrl: str(o.tokenUrl),
    oauthScope: str(o.scope),
    inputRulesText: Array.isArray(c.inputRules) ? JSON.stringify(c.inputRules, null, 2) : '',
    extra,
  };
}

// ── placeholders (BodyTemplate.java) ──────────────────────────────────────

const PLACEHOLDER = /\{\{\s*([a-z]+)\.([A-Za-z0-9_\-.]+)(\?)?((?:\s*\|\s*[a-z_]+(?::[^|}]+)?)*)\s*\}\}/g;
const NAMESPACES = ['lead', 'call', 'agent', 'org', 'now', 'args'];
const CALL_KEYS = ['id', 'caller', 'did'];
const ORG_KEYS = ['id', 'business_id'];
const NOW_KEYS = ['iso', 'epoch_ms'];
const FILTERS = ['digits', 'trim', 'upper', 'lower', 'max'];

/** What the body template textarea offers to insert, grouped as the runbook describes them. */
export const PLACEHOLDER_HELP: { token: string; means: string; fetchOnly?: boolean }[] = [
  { token: '{{lead.name}}', means: 'any captured field — name, phone, email, notes, every form field (fullName = full_name)' },
  { token: '{{call.id}}', means: 'the call id (what makes a push once-only)' },
  { token: '{{call.caller}}', means: 'the calling number, from cpass’s own record of the call' },
  { token: '{{call.did}}', means: 'the number that was called' },
  { token: '{{agent.issueType}}', means: 'a fixed value from the agent’s binding (Agents → Custom API push)' },
  { token: '{{org.id}}', means: 'the organisation id' },
  { token: '{{org.business_id}}', means: 'the business id' },
  { token: '{{now.iso}}', means: 'the time of sending, ISO-8601' },
  { token: '{{now.epoch_ms}}', means: 'the time of sending, epoch milliseconds' },
  { token: '{{args.msisdn}}', means: 'an argument the agent passed to the tool', fetchOnly: true },
];
export const PLACEHOLDER_SYNTAX =
  'Append ? for optional ({{lead.email?}} → null/empty when missing). Filters: |digits |trim |upper |lower |max:N, e.g. {{lead.name|trim|max:200}}. A required value that is missing means nothing is sent.';

const filtersOf = (group: string | undefined): string[] =>
  (group || '').split('|').map(f => f.trim()).filter(Boolean);

/** Problems in one string's placeholders — the same rules as BodyTemplate.checkString. */
export function checkPlaceholders(s: string): string[] {
  const problems: string[] = [];
  let found = 0;
  for (const m of Array.from(s.matchAll(PLACEHOLDER))) {
    found++;
    const [whole, ns, key] = m;
    if (!NAMESPACES.includes(ns)) problems.push(`unknown placeholder namespace '${ns}' in ${whole}`);
    else if (ns === 'call' && !CALL_KEYS.includes(key)) problems.push(`unknown call.${key} (call.id, call.caller, call.did)`);
    else if (ns === 'org' && !ORG_KEYS.includes(key)) problems.push(`unknown org.${key} (org.id, org.business_id)`);
    else if (ns === 'now' && !NOW_KEYS.includes(key)) problems.push(`unknown now.${key} (now.iso, now.epoch_ms)`);
    filtersOf(m[4]).forEach(f => {
      const name = f.includes(':') ? f.slice(0, f.indexOf(':')) : f;
      if (!FILTERS.includes(name)) problems.push(`unknown filter '${name}' in ${whole}`);
      if (name === 'max' && !/^max:\d+$/.test(f)) problems.push('max needs a number, e.g. max:200');
    });
  }
  const opens = s.split('{{').length - 1;
  if (opens > found) problems.push(`malformed placeholder in "${s}"`);
  return problems;
}

/** `ns.key` for every placeholder in a string, in order, once each. */
export function placeholdersIn(s: string): string[] {
  const out: string[] = [];
  for (const m of Array.from(s.matchAll(PLACEHOLDER))) {
    const name = `${m[1]}.${m[2]}`;
    if (!out.includes(name)) out.push(name);
  }
  return out;
}

/** Every string value inside a parsed JSON template. */
const stringsIn = (v: unknown, out: string[] = []): string[] => {
  if (typeof v === 'string') out.push(v);
  else if (Array.isArray(v)) v.forEach(x => stringsIn(x, out));
  else if (isObj(v)) Object.values(v).forEach(x => stringsIn(x, out));
  return out;
};

// ── form → configJson ─────────────────────────────────────────────────────

/** Header names the server refuses: a credential belongs in the encrypted auth fields. */
export const SECRET_HEADER = /authorization|api[-_]?key|token|secret|password|cookie|signature/i;
const TOOL_NAME = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export interface BuiltConfig {
  config: Record<string, unknown>;
  /** What the server would refuse, said before the round trip. Empty = saveable. */
  problems: string[];
  /** Every placeholder the request names (URL + body), for the summary under the form. */
  placeholders: string[];
}

/**
 * The form as configJson, plus what is wrong with it.
 *
 * Only what applies to the chosen mode and auth is written — switching a row from
 * HMAC to a bearer token must not leave an `hmac` block behind that nothing reads.
 * Keys the form does not model (`extra`) are kept verbatim.
 */
export function buildConfig(f: CustomApiForm, authType: CrmAuthType | undefined): BuiltConfig {
  const problems: string[] = [];
  const c: Record<string, unknown> = { ...f.extra };
  const fetch = f.mode === 'FETCH';
  const method = (f.method || (fetch ? 'GET' : 'POST')).toUpperCase();

  c.mode = f.mode;
  if (fetch) {
    c.toolName = f.toolName.trim();
    if (!TOOL_NAME.test(f.toolName.trim())) problems.push('FETCH needs a tool name: lowercase letters, digits, _ or -, at most 64');
  }
  c.method = method;
  const methods = fetch ? ['GET', 'POST', 'PUT'] : ['POST', 'PUT'];
  if (!methods.includes(method)) problems.push(`method must be one of ${methods.join(', ')}`);

  const url = f.url.trim();
  c.url = url;
  const named: string[] = [];
  if (!url) problems.push('url is required');
  else {
    problems.push(...checkPlaceholders(url));
    named.push(...placeholdersIn(url));
    const scheme = url.match(/^([a-z][a-z0-9+.-]*):\/\//i);
    if (!scheme || !['http', 'https'].includes(scheme[1].toLowerCase())) problems.push('url must be an absolute http(s) URL');
    else {
      const authority = url.slice(scheme[0].length).split(/[/?#]/, 1)[0];
      if (!authority) problems.push('url must be an absolute http(s) URL');
      if (authority.includes('{{')) problems.push("the url's host cannot be a placeholder");
    }
  }
  if (f.requestIdParam.trim()) c.requestIdParam = f.requestIdParam.trim();

  const headers = objectFrom(f.headers);
  Object.keys(headers).forEach(k => {
    if (SECRET_HEADER.test(k)) problems.push(`header '${k}' looks like a credential — use the auth fields, which are encrypted`);
  });
  if (Object.keys(headers).length) c.headers = headers;

  if (f.timeoutMs.trim()) {
    const t = Number(f.timeoutMs);
    const max = fetch ? 8000 : 10000;
    c.timeoutMs = t;
    if (!Number.isInteger(t) || t < 500 || t > max) problems.push(`timeout must be 500..${max} ms`);
  }

  c.bodyFormat = f.bodyFormat;
  const needsBody = method !== 'GET';
  const text = f.bodyText.trim();
  if (f.bodyFormat === 'XML') {
    if (text) {
      c.bodyTemplateText = f.bodyText;
      problems.push(...checkPlaceholders(f.bodyText));
      named.push(...placeholdersIn(f.bodyText));
    } else if (needsBody) problems.push('XML needs the body template (the XML document, with placeholders)');
  } else if (text) {
    try {
      const body = JSON.parse(text);
      if (!isObj(body)) problems.push('the body template must be a JSON object');
      else {
        c.bodyTemplate = body;
        stringsIn(body).forEach(s => { problems.push(...checkPlaceholders(s)); named.push(...placeholdersIn(s)); });
        if (f.bodyFormat === 'FORM_URLENCODED') {
          Object.entries(body).forEach(([k, v]) => {
            if (v !== null && typeof v === 'object') problems.push(`a form body must be flat; '${k}' is nested`);
          });
        }
      }
    } catch (e: any) {
      problems.push(`the body template is not valid JSON: ${e.message}`);
    }
  } else if (needsBody) problems.push('the body template must be a JSON object');

  c.responseFormat = f.responseFormat;
  const response: Record<string, string> = {};
  ([['successPath', f.successPath], ['successValue', f.successValue], ['referencePath', f.referencePath],
    ['errorPath', f.errorPath], ['messagePath', f.messagePath]] as const).forEach(([k, v]) => {
    if (v.trim()) response[k] = v.trim();
  });
  if (Object.keys(response).length) c.response = response;

  if (fetch) {
    const fields = objectFrom(f.responseFields);
    if (Object.keys(fields).length) c.responseFields = fields;
    else problems.push('FETCH needs response fields {name: path} — the only values the agent will see');
    if (f.sayOnError.trim()) c.sayOnError = f.sayOnError.trim();
  }
  if (!fetch && named.some(n => n.startsWith('args.'))) problems.push('{{args.*}} exists only in FETCH mode');

  if (!fetch && f.inputRulesText.trim()) {
    try {
      const rules = JSON.parse(f.inputRulesText);
      if (!Array.isArray(rules)) problems.push('input rules must be a JSON array');
      else {
        c.inputRules = rules;
        rules.forEach((r: any) => { if (!isObj(r) || !r.field || !r.pattern) problems.push('each input rule needs field and pattern'); });
      }
    } catch (e: any) {
      problems.push(`input rules are not valid JSON: ${e.message}`);
    }
  }

  if (authType === 'API_KEY' && f.apiKeyHeader.trim()) c.apiKeyHeader = f.apiKeyHeader.trim();
  if (authType === 'HMAC_SHA256') {
    const h: Record<string, string> = { signs: f.hmacSigns, encoding: f.hmacEncoding };
    if (f.hmacHeader.trim()) h.header = f.hmacHeader.trim();
    if (f.hmacTimestampHeader.trim()) h.timestampHeader = f.hmacTimestampHeader.trim();
    if (f.hmacPrefix) h.prefix = f.hmacPrefix;
    c.hmac = h;
  }
  if (authType === 'OAUTH2_CLIENT_CREDENTIALS') {
    const tokenUrl = f.oauthTokenUrl.trim();
    if (!tokenUrl.startsWith('https://')) problems.push('OAuth2 needs a token URL starting https://');
    const o: Record<string, string> = { tokenUrl };
    if (f.oauthScope.trim()) o.scope = f.oauthScope.trim();
    c.oauth = o;
  }

  return { config: c, problems: Array.from(new Set(problems)), placeholders: Array.from(new Set(named)) };
}

/**
 * The server's 400 for a bad config, one problem per line.
 * CustomApiConfig.parse throws "Custom API config: a; b; c".
 */
export function splitServerProblems(message?: string | null): string[] {
  if (!message) return [];
  const m = message.replace(/^\s*Custom API config:\s*/i, '');
  return m === message ? [message] : m.split(/;\s+/).map(s => s.trim()).filter(Boolean);
}

/** PUSH or FETCH, read off a stored row — FETCH rows cannot be bound as a push. */
export const modeOf = (configJson?: string | null): CustomApiMode => formFromConfig(configJson).mode;

// ── the agent binding: config_json key `custom_api_push` ──────────────────

export const PUSH_KEY = 'custom_api_push';

/**
 * One binding (CustomApiPushService.bindings): `{providerId, values, enabled?}`.
 * Anything else a binding carries is kept in `extra` and written back.
 */
export interface PushBinding { providerId: string; values: KV[]; enabled: boolean; hadEnabled: boolean; extra: Record<string, unknown> }

export interface PushBindings {
  bindings: PushBinding[];
  /** Entries that are not objects — the server skips them; this keeps them rather than drop them. */
  opaque: unknown[];
  /** The stored shape was an array; a single binding is then written back as one. */
  wasArray: boolean;
}

export function readBindings(raw: unknown): PushBindings {
  const wasArray = Array.isArray(raw);
  const items: unknown[] = wasArray ? (raw as unknown[]) : raw === undefined || raw === null ? [] : [raw];
  const bindings: PushBinding[] = [];
  const opaque: unknown[] = [];
  items.forEach(it => {
    if (!isObj(it)) { opaque.push(it); return; }
    const { providerId, values, enabled, ...extra } = it;
    bindings.push({
      providerId: str(providerId),
      values: rowsFrom(values),
      // The server skips a binding only when enabled is present AND not "true".
      enabled: enabled === undefined || enabled === null || String(enabled) === 'true',
      hadEnabled: enabled !== undefined,
      extra,
    });
  });
  return { bindings, opaque, wasArray };
}

/** The value to store under `custom_api_push`, or undefined to remove the key. */
export function writeBindings(b: PushBindings): unknown {
  const out: unknown[] = b.bindings.map(x => {
    const o: Record<string, unknown> = { ...x.extra, providerId: x.providerId.trim(), values: objectFrom(x.values) };
    if (!x.enabled || x.hadEnabled) o.enabled = x.enabled;
    return o;
  });
  out.push(...b.opaque);
  if (out.length === 0) return undefined;
  if (out.length === 1 && !b.wasArray) return out[0];
  return out;
}

/** Problems a person can fix before saving a binding. */
export function bindingProblems(b: PushBindings): string[] {
  const p: string[] = [];
  const seen = new Set<string>();
  b.bindings.forEach((x, i) => {
    const id = x.providerId.trim();
    if (!id) p.push(`binding ${i + 1}: pick a provider`);
    else if (seen.has(id)) p.push(`binding ${i + 1}: the same provider is bound twice`);
    seen.add(id);
    const keys = x.values.map(v => v.key.trim()).filter(Boolean);
    if (new Set(keys).size !== keys.length) p.push(`binding ${i + 1}: a value name is repeated`);
  });
  return p;
}

/** A copy of the agent document with ONLY `custom_api_push` changed (or removed). */
export function withPushBinding<T extends Record<string, any>>(doc: T, value: unknown): T {
  const next: Record<string, any> = { ...doc };
  if (value === undefined) delete next[PUSH_KEY];
  else next[PUSH_KEY] = value;
  return next as T;
}

/**
 * Every leaf that differs between two agent documents OUTSIDE `custom_api_push`.
 *
 * The agent PUT replaces config_json wholesale and re-pushes the runtime, so the
 * binding save compares the WHOLE document it is about to send against the one it
 * just read — never a hand-picked subset. Anything listed here means the save
 * would change more than the binding, and it must refuse.
 */
export function changesOutsideBinding(before: unknown, after: unknown): string[] {
  const d = diffDocuments(before, after);
  const inside = (p: string) => p === PUSH_KEY || p.startsWith(`${PUSH_KEY}.`) || p.startsWith(`${PUSH_KEY}[`);
  return [...d.changed, ...d.added, ...d.removed].map(c => c.path).filter(p => !inside(p));
}

// ── deliveries ────────────────────────────────────────────────────────────

/**
 * Who may read deliveries — CustomApiDeliveryController's @PreAuthorize, literally.
 * cpass has no RoleHierarchy, so a role is in or out by name; these are the
 * portal's lower-case spellings of MASTER_ADMIN, PLATFORM_ADMIN, SUPER_ADMIN,
 * SERVICE_ADMIN, PROVIDER_OWNER, PROVIDER_ADMIN.
 */
export const CUSTOM_API_DELIVERY_ROLES =
  ['master_admin', 'platform_admin', 'super_admin', 'service_admin', 'provider_owner', 'provider_admin'];

/** The binding Check (GET …/agents/{id}/check) is narrower: MASTER_ADMIN and PLATFORM_ADMIN only. */
export const CUSTOM_API_CHECK_ROLES = ['master_admin', 'platform_admin'];

/** Each status as someone reading the list needs it (CustomApiDelivery.Status). */
export const DELIVERY_STATUS: Record<CustomApiDeliveryStatus, { label: string; tone: 'ok' | 'wait' | 'retry' | 'bad'; means: string }> = {
  SENT: { label: 'Sent', tone: 'ok', means: 'Accepted by the far end.' },
  AWAITING_CALLER: { label: 'Awaiting caller', tone: 'wait', means: 'Held until the calling number reaches cpass (at hang-up); failed after an hour without it.' },
  PENDING: { label: 'Retrying', tone: 'retry', means: 'No answer yet (timeout, 5xx, 408, 429) — the CRM retry queue retries it with backoff.' },
  SENDING: { label: 'Sending', tone: 'retry', means: 'An attempt is in flight.' },
  REJECTED: { label: 'Rejected', tone: 'bad', means: 'A definite no (success false, or a 4xx). Final — never resent.' },
  UNCONFIRMED: { label: 'Unconfirmed', tone: 'bad', means: '2xx without the success field. It may have been recorded, so it is never resent.' },
  INVALID: { label: 'Invalid', tone: 'bad', means: 'A required placeholder had no value. Nothing was sent.' },
  FAILED: { label: 'Failed', tone: 'bad', means: 'No answer after the last attempt, or the caller never became known.' },
};

export const DELIVERY_STATUS_ORDER: CustomApiDeliveryStatus[] =
  ['SENT', 'AWAITING_CALLER', 'PENDING', 'SENDING', 'REJECTED', 'UNCONFIRMED', 'INVALID', 'FAILED'];

/** Client-side narrowing the endpoint does not offer (it filters by org, status and day only). */
export function matchesDelivery(
  d: { providerId?: string; agentId?: number | null; callId?: string | null },
  f: { providerId?: string; agentId?: string; callId?: string },
): boolean {
  if (f.providerId && d.providerId !== f.providerId) return false;
  if (f.agentId && String(d.agentId ?? '') !== f.agentId.trim()) return false;
  if (f.callId && !String(d.callId ?? '').includes(f.callId.trim())) return false;
  return true;
}
